<template>
  <Teleport to="body">
    <div class="puzzle-fullscreen" :class="{ 'puzzle-fading': fading, 'puzzle-walk-mode': walkMode }">
      <div class="puzzle-content">
        <div v-if="walkMode && puzzleIndex != null && puzzleTotal != null" class="puzzle-counter">
          <span v-if="traceMode && isSolvedReplay" class="puzzle-counter-text puzzle-counter-trace">
            <span class="trace-square trace-square-solved-green" />
          </span>
          <span v-else-if="traceMode && tracePhase === 'correct'" class="puzzle-counter-text puzzle-counter-trace">
            <span class="trace-square trace-square-solved-green" />
          </span>
          <span v-else-if="traceMode && tracePhase === 'wrong'" class="puzzle-counter-text puzzle-counter-trace">
            <span class="trace-square trace-square-mine-private" />
          </span>
          <span v-else-if="traceMode" class="puzzle-counter-text puzzle-counter-trace">
            <span
              class="trace-square"
              :class="
                isMyTrace ? (isTracePrivate ? 'trace-square-mine-private' : 'trace-square-mine') : 'trace-square-other'
              "
            />
          </span>
          <span v-else class="puzzle-counter-text">
            <span v-if="puzzleType === 'stop'" class="puzzle-counter-emoji">💡</span>
            <span class="puzzle-counter-number">
              <img :src="puzzleIcon" class="puzzle-counter-icon" alt="" />
              {{ puzzleIndex }}
            </span>
          </span>
        </div>

        <div class="puzzle-board-wrapper">
          <div v-if="!puzzle.isReady.value && !puzzle.isSolved.value && !walkMode" class="puzzle-loading">
            <div class="loading-text">{{ localize('puzzle.loading') }}</div>
          </div>

          <Transition :name="boardTransitionName" mode="out-in">
            <GamChessboard
              :key="boardKey"
              :fen="puzzle.boardFen.value"
              :mode="GamChessboardMode.PLAY"
              :orientation="puzzle.orientation.value"
              :is-interactive="isInteractive"
              :last-move-from="puzzle.lastMoveFrom.value"
              :last-move-to="puzzle.lastMoveTo.value"
              is-animated
              skip-promotion
              @move="handleMove"
              @blocked-touch="onBlockedTouch"
            />
          </Transition>

          <Transition name="fade">
            <div v-if="puzzle.isWrong.value && !walkMode" class="wrong-indicator" />
          </Transition>

          <Transition name="fade">
            <div v-if="showWalkWrongIndicator" class="wrong-indicator" />
          </Transition>

          <Transition name="fade">
            <div v-if="userSolved && puzzle.isReady.value && !walkMode" class="solved-overlay" />
          </Transition>

          <Transition name="fade">
            <div v-if="showWalkSolvedOverlay" class="solved-overlay" />
          </Transition>

          <Transition name="fade">
            <div v-if="walkSubmitState === 'submitting'" class="submit-overlay">
              <span class="submit-overlay-text">{{ localize('walk.puzzle.submitting') }}</span>
            </div>
          </Transition>

          <Transition name="fade">
            <div
              v-if="walkSubmitState === 'update_location'"
              class="submit-overlay submit-overlay-action"
              @click="emit('update-location')"
            >
              <span class="submit-overlay-text">{{ localize('walk.puzzle.update_location') }}</span>
            </div>
          </Transition>

          <Transition name="fade">
            <div v-if="showPlayCta" class="trace-play-overlay" @touchend.prevent="handlePlayCta" @click="handlePlayCta">
              <span class="trace-play-btn">
                <GamIcon :name="GamIconName.PLAY_EMPTY" size="28px" />
              </span>
            </div>
          </Transition>

          <Transition name="fade">
            <div
              v-if="showRetryCta"
              class="trace-retry-overlay"
              @touchend.prevent="!traceRetryLoading && emit('trace-retry')"
              @click="!traceRetryLoading && emit('trace-retry')"
            >
              <span class="trace-retry-btn" :class="{ 'trace-retry-loading': traceRetryLoading }">
                <GamIcon :name="GamIconName.REFRESH" size="28px" />
              </span>
            </div>
          </Transition>
        </div>

        <div class="puzzle-bar">
          <div class="turn-indicator" :style="{ visibility: showTurnIndicator ? 'visible' : 'hidden' }">
            <div :class="['turn-dot', puzzle.playerColor.value === 'w' ? 'white' : 'black']" />
            <span class="turn-label">{{
              puzzle.playerColor.value === 'w'
                ? localize('chessboard.demo.white_to_move')
                : localize('chessboard.demo.black_to_move')
            }}</span>
          </div>
        </div>

        <div
          v-if="!walkMode"
          class="puzzle-status"
          :style="{ visibility: puzzle.isWrong.value ? 'visible' : 'hidden' }"
        >
          <span class="status-wrong">{{ localize('puzzle.try_again') }}</span>
        </div>

        <div
          v-if="walkMode"
          class="puzzle-close-btn"
          @touchend.prevent="emit('keep-walking')"
          @click="emit('keep-walking')"
        >
          <span class="puzzle-close-x">✕</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import GamChessboard from '@/views/components/gam-chessboard/GamChessboard.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamChessboardMode } from '@/views/composables/constants/components/gamChessboard.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamComponentsEmits } from '@/views/composables/constants/main/emit.constants';
import { usePuzzleEngine, type PuzzleData, type TracePhase } from '@/views/composables/hooks/usePuzzleEngine';
import type { ChessboardMoveEvent } from '@/views/composables/models/components/GamChessboard';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

const puzzleIcon = new URL('../../../ui/icons/puzzle-grid.svg', import.meta.url).href;

const props = defineProps<{
  puzzleData: PuzzleData | null;
  walkMode?: boolean;
  viewOnly?: boolean;
  traceMode?: boolean;
  isMyTrace?: boolean;
  isTracePrivate?: boolean;
  isSolvedReplay?: boolean;
  tracePhase?: TracePhase | null;
  traceRetryLoading?: boolean;
  walkResult?: 'correct' | 'wrong' | null;
  walkSubmitState?: 'idle' | 'submitting' | 'update_location';
  boardResetKey?: number;
  puzzleIndex?: number;
  puzzleTotal?: number;
  puzzleType?: string | null;
  fading?: boolean;
}>();

const emit = defineEmits([
  GamComponentsEmits.CLOSE,
  'solved',
  'keep-walking',
  'wrong-move',
  'submit-move',
  'update-location',
  'retry-proximity',
  'play-replay',
  'trace-retry',
]);

const boardKey = ref(0);
const boardTransitionName = ref('puzzle-swap');
const userSolved = ref(false);
const displayedPuzzle = ref(props.puzzleData);

function onBlockedTouch() {
  if (props.tracePhase === 'wrong') {
    emit('trace-retry');
    return;
  }
  if (!props.traceMode) return;
}

const isInteractive = computed(() => {
  if (props.tracePhase != null) {
    return props.tracePhase === 'interactive' && puzzle.canInteract.value;
  }
  return (
    !props.viewOnly &&
    puzzle.canInteract.value &&
    props.walkResult !== 'wrong' &&
    props.walkResult !== 'correct' &&
    (!props.walkSubmitState || props.walkSubmitState === 'idle')
  );
});

const showWalkWrongIndicator = computed(() => {
  if (props.tracePhase != null) return props.tracePhase === 'wrong';
  return props.walkResult === 'wrong' || (props.walkMode && puzzle.isWrong.value);
});

const showWalkSolvedOverlay = computed(() => {
  if (props.tracePhase != null) return props.tracePhase === 'correct';
  return (
    props.walkResult === 'correct' || (props.walkMode && puzzle.isSolved.value && !(props.traceMode && props.isMyTrace))
  );
});

const playCtaDismissed = ref(false);
const showPlayCta = computed(() => props.tracePhase === 'play_cta' && !playCtaDismissed.value);

const retryCtaReady = ref(false);
const showRetryCta = computed(() => props.tracePhase === 'wrong' && retryCtaReady.value);

const showTurnIndicator = computed(() => {
  if (props.tracePhase != null) return props.tracePhase === 'interactive' && puzzle.canInteract.value;
  return puzzle.isReady.value && !puzzle.isSolved.value && props.walkResult !== 'wrong';
});

watch(
  () => props.tracePhase,
  (phase) => {
    playCtaDismissed.value = false;
    retryCtaReady.value = false;
    if (phase === 'wrong') {
      setTimeout(() => {
        retryCtaReady.value = true;
      }, 600);
    }
  },
);

function handlePlayCta() {
  playCtaDismissed.value = true;
  setTimeout(() => {
    puzzle.playReplayMove();
    setTimeout(() => {
      emit('play-replay', puzzle.replayWasCorrect.value);
    }, 600);
  }, 600);
}

const puzzleDataComputed = computed((): PuzzleData | null => {
  return (props.walkMode ? displayedPuzzle.value : props.puzzleData) ?? null;
});

const puzzle = usePuzzleEngine(() => puzzleDataComputed.value);

watch(
  () => puzzle.isLocked.value,
  (locked, wasLocked) => {
    if (wasLocked && !locked && !props.walkMode) {
      boardKey.value++;
    }
  },
);

watch(
  () => props.puzzleData?.id,
  (newId, oldId) => {
    if (newId != null && newId !== oldId) {
      if (props.walkMode) {
        setTimeout(() => {
          displayedPuzzle.value = props.puzzleData;
          boardKey.value++;
          userSolved.value = false;
        }, 800);
      } else {
        displayedPuzzle.value = props.puzzleData;
        boardKey.value++;
        userSolved.value = false;
      }
    }
  },
);

watch(
  () => props.boardResetKey,
  () => {
    if (props.walkMode) {
      const skipTransition = !!props.puzzleData?.skipSetupAnimation;
      if (skipTransition) {
        boardTransitionName.value = '';
        displayedPuzzle.value = props.puzzleData;
        boardKey.value++;
        nextTick(() => {
          boardTransitionName.value = 'puzzle-swap';
        });
      } else {
        displayedPuzzle.value = null;
        nextTick(() => {
          displayedPuzzle.value = props.puzzleData;
          boardKey.value++;
        });
      }
    } else {
      boardKey.value++;
    }
  },
);

watch(
  () => props.puzzleData?.hasSolved,
  (hasSolved, hadSolved) => {
    if (hasSolved && !hadSolved) {
      userSolved.value = true;
      if (!props.walkMode) {
        boardKey.value++;
      }
    }
  },
);

async function handleMove(event: ChessboardMoveEvent) {
  if (props.walkMode) {
    const uci = event.from + event.to + (event.promotion || '');
    if (puzzleDataComputed.value?.solutionMove) {
      puzzle.checkMove(event.from, event.to, event.promotion);
    } else {
      puzzle.isLocked.value = true;
    }
    emit('submit-move', uci);
    return;
  }

  const solved = puzzle.checkMove(event.from, event.to, event.promotion);

  if (solved && puzzleDataComputed.value) {
    userSolved.value = true;
    emit('solved');
  }
}

let autoDismissTimer: ReturnType<typeof setTimeout> | null = null;

watch(
  () => userSolved.value,
  (solved) => {
    if (solved && props.walkMode && props.tracePhase == null) {
      autoDismissTimer = setTimeout(() => {
        emit('keep-walking');
      }, 3500);
    }
  },
);

onBeforeUnmount(() => {
  if (autoDismissTimer) clearTimeout(autoDismissTimer);
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.puzzle-fullscreen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(23, 23, 23, 0.75);
  padding: var(--spacing-sm);
  transition: opacity 0.8s ease;

  &.puzzle-fading {
    opacity: 0;
  }
}

.puzzle-content {
  width: min(100vw - 16px, 100vh - 120px, 560px);
}

.puzzle-counter {
  margin-top: auto;
}

.puzzle-counter {
  text-align: center;
  margin-bottom: var(--spacing-xl);

  .puzzle-counter-text {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 0.02em;
  }

  .puzzle-counter-emoji {
    position: absolute;
    right: 100%;
    margin-right: 6px;
    font-size: 22px;
    font-weight: 400;
  }

  .puzzle-counter-number {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font: inherit;
    color: inherit;
    letter-spacing: inherit;
  }

  .puzzle-counter-icon {
    width: 20px;
    height: 20px;
    display: block;
  }

  .puzzle-counter-trace {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .trace-square {
      width: 24px;
      height: 24px;
      border-radius: 5px;
      display: block;

      &.trace-square-mine {
        background-color: oklch(0.7818 0.2418 355);
      }

      &.trace-square-mine-private {
        background-color: #e74c3c;
      }

      &.trace-square-other {
        background-color: #f5c542;
      }

      &.trace-square-solved-green {
        background-color: #2ecc71;
      }
    }
  }
}

.puzzle-board-wrapper {
  position: relative;
  border-radius: var(--radius-normal);
  overflow: hidden;

  .puzzle-walk-mode & {
    opacity: 0;
    animation: puzzle-board-fade-in 0.8s ease 0.5s forwards;
  }

  :deep(.gam-chessboard-wrapper) {
    max-width: none;
  }
}

.puzzle-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background: rgba(0, 0, 0, 0.4);
}

.loading-text {
  font-size: 14px;
  color: var(--color-white-60);
}

.wrong-indicator {
  position: absolute;
  inset: 0;
  z-index: 3;
  background: rgba(231, 76, 60, 0.25);
  pointer-events: none;
}

.solved-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  background: rgba(46, 204, 113, 0.25);
  pointer-events: none;
}

.submit-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: none;

  &.submit-overlay-action {
    cursor: pointer;
    pointer-events: auto;
  }
}

.submit-overlay-text {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-white-80);
  padding: 10px 20px;
  border-radius: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid var(--color-white-20);
  backdrop-filter: blur(8px);
}

.trace-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  cursor: pointer;
}

.trace-play-btn {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid var(--color-white-20);
  backdrop-filter: blur(8px);
  color: var(--color-white);
  transition: all 200ms ease;
  touch-action: manipulation;

  @include hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: var(--color-white-30);
  }
}

.trace-retry-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  cursor: pointer;
}

.trace-retry-btn {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid var(--color-white-20);
  backdrop-filter: blur(8px);
  color: var(--color-white);
  transition: all 200ms ease;
  touch-action: manipulation;

  @include hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: var(--color-white-30);
  }

  &.trace-retry-loading {
    animation: retry-spin 0.8s linear infinite;
    opacity: 0.6;
    pointer-events: none;
  }
}

@keyframes retry-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.puzzle-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-top: var(--spacing-md);
  min-height: 36px;
}

.turn-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.turn-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  transition: background 0.2s ease;

  &.white {
    background: var(--color-white);
    box-shadow: 0 0 6px var(--color-white-50);
  }

  &.black {
    background: #000;
    border: 1px solid var(--color-white-30);
  }
}

.turn-label {
  color: var(--color-white-70);
  font-size: 13px;
  font-weight: 500;
}

.puzzle-status {
  padding-top: var(--spacing-sm);
  min-height: 28px;
}

.status-wrong {
  color: #e74c3c;
  font-weight: 600;
  font-size: 14px;
}

.puzzle-close-btn {
  margin: var(--spacing-md) auto 0;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  .puzzle-close-x {
    font-size: 15px;
    line-height: 1;
  }

  @include press;
}

.puzzle-swap-enter-active,
.puzzle-swap-leave-active {
  transition: opacity 0.5s ease;
}

.puzzle-swap-enter-from,
.puzzle-swap-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes puzzle-board-fade-in {
  to {
    opacity: 1;
  }
}
</style>
